import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { CButton, CCol, CRow, CForm } from '@coreui/react';
import { AIInsightsDetailContext } from '../Popup/AIInsightsTemplate';
import StepCardHeader from '../Components/StepCardHeaders';
import Select from 'react-select';
import CIcon from '@coreui/icons-react';
import Accordion from '../../../../../general/accordion';
import InstructionsPreview from './InstructionsPreview';
import DataPreview from './DataPreview';
import { Formik } from 'formik';
import {
     API_AI_INSIGHTS_JOB_BY_ID,
     API_AI_INSIGHTS_RUN_ANALYSIS,
     API_AI_INSIGHTS_TEMPLATES_BY_ID,
     DEFAULT_DATE_FORMAT,
     ROLLING_DATE_RANGE,
     SAVE_REPORT_DATE_OPTION,
} from '../../../../../../constants';
import { callTokenApi } from '../../../../../../apiCaller';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import FormikStateObserver from '../Components/FormikStateObserver';
import { subscribe, unsubscribe } from '../../../../../../containers/subscriber/eventBus';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Waiting from '../../../../../general/Loadings/Waiting';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import ViewMarkdown from '../Components/ViewMarkdown';
import { ClickInfoIconTooltip, HoverInfoIconTooltip } from '../../../../../general/custom-tooltips';
import { TRIGGER_TYPE } from '../../../../../../constants/aiInsights';
import CFormGroup from '../../../../../migration/CFormGroup';
import { CInputCheckbox } from '../../../../../migration/CInput';
import CLabel from '../../../../../migration/CLabel';
import { AI_INTENT_TYPES } from '../../../../../../constants/intent-rules';
import { fetchAiInsightAvailableReports, buildReportOptionLabel } from '../../../../../../utils/aiInsightDataSources';

dayjs.extend(utc);
dayjs.extend(timezone);

const ACCORDION_TYPES = {
     INSTRUCTION: 'Instructions',
     DATA: 'Data',
};

const LLM_ALWAYS_USE_REASONING = ['openai/gpt-oss-120b' , 'openai/gpt-oss-20b']

const ModelSelect = React.memo(({ value, options, modelOptions, onChange, id, disabled, enabledReasoning, onReasoningChange, idPrefix = '' }) => {
     const selectedModel = modelOptions.find((opt) => opt.value === value);
     
     const isAlwaysUseReasoning = LLM_ALWAYS_USE_REASONING.includes(selectedModel?.name);
     
     // Determine checkbox state
     const isReasoningDisabled = !selectedModel?.supportReasoning || isAlwaysUseReasoning;
     const showReasoningTooltip = selectedModel && "This model supports reasoning. Enabling it should improve the results of your analysis by instructing the model to think through your analysis. This will cause the number of AI Credits used to increase."
     
     if (isAlwaysUseReasoning && !enabledReasoning && value) {
          setTimeout(() => onReasoningChange(true), 0);
     }
     
     const checkboxId = `${idPrefix}${value}-reasoning`;
     
     return (
          <div className='d-flex align-items-center'>
               <div className='flex-grow-1' style={{ width: '100%', maxWidth: '300px' }}>
                    <Select
                         classNamePrefix="react-select"
                         id={id}
                         name={id}
                         value={selectedModel}
                         options={options}
                         onChange={(e) => onChange(e.value)}
                         isDisabled={disabled}
                    />
               </div>
               {
                    selectedModel?.supportReasoning && (
                    <CFormGroup variant="custom-checkbox" inline className='ml-2'>
                         <CInputCheckbox
                              custom
                              id={checkboxId}
                              name={checkboxId}
                              checked={isAlwaysUseReasoning || enabledReasoning}
                              className="m-auto"
                              onChange={(e) => onReasoningChange(e.target.checked)}
                              disabled={isReasoningDisabled || disabled}
                         />
                         <CLabel htmlFor={checkboxId} variant="custom-checkbox">
                              Enable reasoning <HoverInfoIconTooltip>{showReasoningTooltip}</HoverInfoIconTooltip>
                         </CLabel>
                    </CFormGroup>
                    )
               }
          </div>
     );
});

const ModelOutput = React.memo(({ output, isLoading, executedPrompt, chargedCredits, inputTokens, outputTokens, jobId }) => {
     const totalTokens = Number(inputTokens || 0) + Number(outputTokens || 0);
     const [showOutput, setShowOutput] = useState(true);
     const [showPrompt, setShowPrompt] = useState(false);
     const intelligenceSetting = useSelector((state) => state.subscriber.intelligenceSetting);

     const toggleOutput = () => {
          setShowOutput((prev) => !prev);
     };

     const togglePrompt = () => {
          setShowPrompt((prev) => !prev);
     };

     if (isLoading) {
          return (
               <div>
               </div>
          );
     }

     const enableShowPrompt = localStorage.getItem('show-prompt') || window.location.search.includes('show-prompt=1');
     const enableShowCost = localStorage.getItem('show-llm-costs') || window.location.search.includes('show-llm-costs=1');

     return output ? (
          <div className="prompt-content">
               <p className="font-weight-bold">
                    <CButton className="d-inline-flex align-items-center text-capitalize pl-0" onClick={toggleOutput}>
                         <CIcon icon={showOutput ? 'iconEyeRegularSlash' : 'iconEyeRegular'} className="icon-eye text-primary mr-2" />
                         <span style={{ textDecoration: 'underline' }}>Preview Output</span>
                    </CButton>
                    {enableShowPrompt &&
                         <CButton className="d-inline-flex align-items-center text-capitalize ml-2" onClick={togglePrompt}>
                              <CIcon icon={showPrompt ? 'iconEyeRegularSlash' : 'iconEyeRegular'} className="icon-eye text-primary mr-2" />
                              <span style={{ textDecoration: 'underline' }}>View Prompt</span>
                         </CButton>
                    }
                    {chargedCredits && intelligenceSetting?.aiBillingCycle && (
                         <div className="ml-2 d-inline-flex align-items-center hover-show-tooltip">
                              <div className='tooltip-wrapper text-primary'>
                                   <HoverInfoIconTooltip iconName="iconDollarSign">
                                        <p>Number of AI Credits Used: {chargedCredits}</p>
                                        {
                                             enableShowCost && (
                                                  <>
                                                       <p>Number of Input Tokens: {Number(inputTokens).toLocaleString('en-US')}</p>
                                                       <p>Number of Output Tokens: {Number(outputTokens).toLocaleString('en-US')}</p>
                                                       <p>Total Tokens: {Number(totalTokens).toLocaleString('en-US')}</p>
                                                  </>
                                             )
                                        }
                                   </HoverInfoIconTooltip>
                              </div>
                         </div>
                    )}
               </p>

               {showPrompt && <ViewMarkdown title="Prompt" markdown={executedPrompt} isCsv={true} />}

               {showOutput && <ViewMarkdown title="Preview Output" markdown={output} jobId={jobId} />}
          </div>
     ) : null;
});

const processDataDescriptions = (dataSources = [], ownAccountId) => {
     const baseText = `I have provided ${dataSources.length} data report${dataSources.length === 1 ? '' : 's'}, each one described below.\n\n`;

     return dataSources.reduce((acc, item, index) => {
          const descriptionData = parseDescriptionOverride(item.descriptionOverride);
          const displayData = {
               ...descriptionData,
               reportName: buildReportOptionLabel({
                    name: descriptionData.reportName,
                    accountName: item.accountName,
                    isCrossAccount: !!ownAccountId && item.accountId !== ownAccountId,
               }),
          };
          return acc + generateReportString(displayData, index) + '\n\n';
     }, baseText);
};

const parseDescriptionOverride = (override) => {
     try {
          return typeof override === 'string' ? JSON.parse(override) : override || {};
     } catch (e) {
          console.error('Error parsing descriptionOverride:', e);
          return {};
     }
};

const generateReportString = (data, index) => {
     const mdParts = [];

     const defaultSegmentDesc = '(no description)';
     const defaultDimensionDesc = '(no description)';
     const defaultMetricDesc = '(no description)';
     const defaultModelDesc = '(no description)';

     const reportDetails = [];

     reportDetails.push(`- **Data Report Name:** ${data.reportName || 'Report name'}`);

     reportDetails.push(`- **Description:** ${data.reportDescription || ''}`);

     if (reportDetails.length) {
          mdParts.push(`### Data Report ${index + 1}`);
          reportDetails.forEach((detail) => {
               mdParts.push(detail);
          });
     }
    
     const filterReportSegment = data.reportSegment?.filter((item) => !item.isExcluded);
     const filterReportModels = data.reportModels?.filter((item) => !item.isExcluded);
     const filterReportDimensions = data.reportDimensions?.filter((item) => !item.isExcluded);
     const filterReportMetrics = data.reportMetrics?.filter((item) => !item.isExcluded);

     if (filterReportSegment?.length) {
          const examplePrefixes = Array.from({ length: filterReportSegment?.length }, (_, i) => `S${i + 1}`);
          let exampleString = examplePrefixes.join(' or ');

          mdParts.push(`**Segments:**`);
          mdParts.push(
               `The data includes segments, which are filters that limit the returned data. Each column belongs to a segment and is prefixed with the segment number such as ${exampleString}:`
          );
          filterReportSegment.forEach((seg, i) => {
               const name = seg.segmentName;
               const description = seg.segmentDescription || defaultSegmentDesc;
               mdParts.push(`  - S${i + 1}: ${name}: ${description}`);
          });
     }

     if (filterReportModels?.length) {
          mdParts.push(`**Models:**`);
          filterReportModels.forEach((model) => {
               const name = model.modelName;
               const description = model.modelDescription || model.modelDefaultDescription || defaultModelDesc;
               mdParts.push(`  - ${name}: ${description}`);
          });
     }

     if (filterReportDimensions?.length) {
          mdParts.push(`**Objects and Dimensions:**`);
          filterReportDimensions.forEach((seg) => {
               const name = seg.dimensionName;
               const description = seg.dimensionDescription || defaultDimensionDesc;
               mdParts.push(`  - ${name}: ${description}`);
          });
     }

     if (filterReportMetrics?.length) {
          mdParts.push(`**Fields:**`);
          filterReportMetrics.forEach((seg) => {
               const name = seg.metricName;
               const description = seg.metricDescription || defaultMetricDesc;
               mdParts.push(`  - ${name}: ${description}`);
          });
     }
     return mdParts.join('\n\n');
};

const generateMainInstructions = (values) => {
     let textToShow = ``;
     if (values) {
          textToShow += `${values}`;
     }
     return textToShow.trimEnd();
};

const generateContext = (values) => {
     try {
          const parts = [];
          if (values.businessDescription && !values.isBusinessDescriptionExcluded) {
               parts.push(values.businessDescription);
          }
          if (values.customerProfile && !values.isCustomerProfileExcluded) {
               parts.push(values.customerProfile);
          }
          
          return parts.map((part) => `${part}\n`).join('\n');
     } catch (e) {
          console.error('Error generating context:', e);
          return '';
     }
};

const generateRulesAndFilters = (stepsData) => {
     if (stepsData.isRulesAndFiltersExcluded) {
          return false;
     }

     let textToShow = ``;
     if (stepsData.rulesAndFilters) {
          textToShow += `${stepsData.rulesAndFilters}`;
     }

     return textToShow.trimEnd();
};

const generatedInsightMarkdown = (values) => {
     const { guidanceOverride, mainInstructions, exampleOutputs, dataDescriptions, context, rulesAndFilters, isExcludeExampleOutput } = values;
     const mdPieces = [];
     if (guidanceOverride) mdPieces.push(`**Main Guidance**\n\n${guidanceOverride}`);
     if (mainInstructions) mdPieces.push(`**Main Instructions**\n\n${mainInstructions}`);
     if (dataDescriptions) mdPieces.push(`**Data Descriptions**\n\n${dataDescriptions}`);
     if (exampleOutputs && !isExcludeExampleOutput) mdPieces.push(`**Example Outputs**\n\n${exampleOutputs}`);
     if (context) mdPieces.push(`**Context**\n\n${context}`);
     if (rulesAndFilters) mdPieces.push(`**Rules/Filters**\n\n${rulesAndFilters}`);

     return mdPieces.join('\n\n');
};

const parseReportName = (descriptionOverride) => {
     try {
          return JSON.parse(descriptionOverride)?.reportName || 'Unnamed Report';
     } catch (e) {
          console.error('Failed to parse report name:', e);
          return 'Unnamed Report';
     }
};

const ModelSelectionPreview = ({ models }) => {
     const { activeStep, setActiveStep, stepsData, setStepsData, handleSetUnsavedChanges, clearUnsavedChangesFlag, state, setState, setIsReFreshLogs} =
          useContext(AIInsightsDetailContext);
     const { timeZone, accountId } = useSelector((state) => state.subscriber.activeAccount);
     const user = useSelector((state) => state.theme.user);
     const safeTimeZone = timeZone || 'UTC';
     const [files, setFiles] = useState([null]);
     const [usingReportFile, setUsingReportFile] = useState(false);
     const userId = useSelector((state) => state.subscriber.activeAccount.userId);
     // The active account's own `savedReport` redux slice never contains
     // cross-account reports, so it can't resolve those rows. Fetch the same
     // allowlist-aware list DataSources.js and DataPreview.js already fetch,
     // keyed on the template id, so every row - own or cross-account - resolves here too.
     const [availableReports, setAvailableReports] = useState([]);
     // True while the available-reports fetch above is in flight. Run Analysis
     // sends reportConfigs (derived from availableReports via the dataReports
     // memo) to the server to override each report's date range, so submitting
     // before this resolves would silently run the analysis against the
     // yesterday->today fallback instead of the configured rolling range.
     const [isLoadingAvailableReports, setIsLoadingAvailableReports] = useState(false);

     useEffect(() => {
          if (!accountId || !stepsData?.id) return;

          let cancelled = false;

          setIsLoadingAvailableReports(true);
          fetchAiInsightAvailableReports({
               templateId: stepsData.id,
               accountId,
               userId: user?.id,
          }).then((reports) => {
               if (cancelled) return;
               setAvailableReports(reports);
               setIsLoadingAvailableReports(false);
          });

          return () => { cancelled = true; };
     }, [accountId, stepsData?.id, user?.id]);

     const getDateRange = (rolling, dayjsNow) => {
          if (!rolling) return null;
          const { type: rollingType, numberPrevious } = rolling;
          const lastWeek = dayjsNow.subtract(1, 'week');
          const lastMonth = dayjsNow.subtract(1, 'month');
          const lastDay = dayjsNow['$D'] === 1 ? dayjsNow.subtract(1, 'day') : dayjsNow;
          const yesterday = dayjs(dayjsNow).subtract(1, 'day');

          const dateRanges = {
               [ROLLING_DATE_RANGE.previous_week_mon_sun]: {
                    startDate: dayjs(lastWeek.startOf('week')).add(1, 'day'),
                    endDate: dayjs(lastWeek.endOf('week')).add(1, 'day'),
               },
               [ROLLING_DATE_RANGE.previous_week_sat_sun]: {
                    startDate: dayjs(lastWeek.startOf('week')).add(6, 'day'),
                    endDate: dayjs(lastWeek.endOf('week')).add(1, 'day'),
               },
               [ROLLING_DATE_RANGE.previous_week_fri_sun]: {
                    startDate: dayjs(lastWeek.startOf('week')).add(5, 'day'),
                    endDate: dayjs(lastWeek.endOf('week')).add(1, 'day'),
               },
               [ROLLING_DATE_RANGE.previous_week_sun_sat]: {
                    startDate: dayjs(lastWeek.startOf('week')),
                    endDate: dayjs(lastWeek.endOf('week')),
               },
               [ROLLING_DATE_RANGE.previous_month]: {
                    startDate: dayjs(lastMonth.startOf('month')),
                    endDate: dayjs(lastMonth.endOf('month')),
               },
               [ROLLING_DATE_RANGE.month_to_date_yesterday]: {
                    startDate: dayjs(lastDay.startOf('month')),
                    endDate: yesterday,
               },
               [ROLLING_DATE_RANGE.year_to_date_yesterday]: {
                    startDate: dayjs(lastDay.startOf('year')),
                    endDate: yesterday,
               },
               [ROLLING_DATE_RANGE.previous_x_days]: {
                    startDate: dayjs(dayjsNow).subtract(numberPrevious, 'day'),
                    endDate: yesterday,
               },
               [ROLLING_DATE_RANGE.previous_x_weeks]: {
                    startDate: dayjs(dayjsNow).subtract(numberPrevious, 'week'),
                    endDate: yesterday,
               },
               [ROLLING_DATE_RANGE.quarter_to_date]: {
                    startDate: dayjs(lastDay.startOf('quarter')),
                    endDate: yesterday,
               },
          };

          return dateRanges[rollingType];
     };

     const dataReports = useMemo(() => {
          if (!stepsData.dataSources?.length) return [];

          return stepsData.dataSources.map((dataSource) => {
               const matchedReport = availableReports.find((report) => report.id === dataSource.reportViewId);
               const dateRangeConfig = matchedReport?.content?.dateRangeConfig;
               // Each report's "now" must be computed in its own account's timezone,
               // not the active account's - otherwise a cross-account report's rolling
               // range is computed against the wrong clock. For own-account reports
               // accountTimeZone equals the active account's timeZone, so this falls
               // back to the exact same value as before.
               const reportTimeZone = matchedReport?.accountTimeZone || safeTimeZone;
               const reportDayjsNow = dayjs().tz(reportTimeZone);

               let startDate = null;
               let endDate = null;

               if (dateRangeConfig?.type === SAVE_REPORT_DATE_OPTION.ROLLING_RANGE) {
                    const range = getDateRange(dateRangeConfig.rolling, reportDayjsNow);
                    startDate = range?.startDate;
                    endDate = range?.endDate;
               }

               return {
                    id: dataSource.id,
                    reportViewId: dataSource.reportViewId,
                    name: parseReportName(dataSource.descriptionOverride),
                    startDate: dayjs(startDate).isValid()
                         ? dayjs(startDate).format(DEFAULT_DATE_FORMAT)
                         : reportDayjsNow.subtract(1, 'day').format(DEFAULT_DATE_FORMAT),
                    endDate: dayjs(endDate).isValid() ? dayjs(endDate).format(DEFAULT_DATE_FORMAT) : reportDayjsNow.format(DEFAULT_DATE_FORMAT),
               };
          });
     }, [stepsData.dataSources, availableReports, safeTimeZone]);

     useEffect(() => {
          setReportConfigs(dataReports);
     }, [dataReports]);

     const [reportConfigs, setReportConfigs] = useState(dataReports);

     const modelOptions = useMemo(() => models.filter(item => item.appliedTypes?.includes(AI_INTENT_TYPES.AI_INSIGHT)).map((item) => ({ value: item.id, label: item.abbreviation, name: item.name, supportReasoning: item.supportReasoning })), [models]);

     const initialValues = useMemo(
          () => ({
               guidanceOverride:
                    stepsData?.guidanceOverride ||
                    `Use the provided data reports to generate insights based on my **"Main Instructions"**. Use the **"Data Descriptions"** to understand the data reports, and the **"Example Outputs"** to generate insights. Use the **"Context"** and **"Rules/Filters"** to improve the relevance and accuracy of the insights you create.`,
               mainInstructions: generateMainInstructions(stepsData?.mainInstructions) || '',
               dataDescriptions: processDataDescriptions(stepsData?.dataSources, stepsData?.accountId) || '',
               exampleOutputs: stepsData?.exampleOutputs || '',
               context: generateContext(stepsData) || '',
               rulesAndFilters: generateRulesAndFilters(stepsData) || '',
               llmModelId: stepsData?.llmModelId || '',
               isBusinessDescriptionExcluded: stepsData.isBusinessDescriptionExcluded,
               isCustomerProfileExcluded: stepsData.isCustomerProfileExcluded,
               isRulesAndFiltersExcluded: stepsData.isRulesAndFiltersExcluded,
               isExcludeExampleOutput: stepsData.isExcludeExampleOutput,
               isIncludeReportReferencesFooter: stepsData.isIncludeReportReferencesFooter,
               isIncludeReportReferencesInline: stepsData.isIncludeReportReferencesInline,
               enabledReasoning: stepsData.enabledReasoning || false,
          }),
          [stepsData] // eslint-disable-line
     );

     const handleModelChange = useCallback(
          (index, selectedValue) => {
               const selectedModel = modelOptions.find(opt => opt.value === selectedValue);
               const isAlwaysUseReasoning = LLM_ALWAYS_USE_REASONING.includes(selectedModel?.value);
               
               setState((prev) => ({
                    ...prev,
                    modelSelections: prev.modelSelections.map((item, i) => (i === index ? { 
                         ...item, 
                         id: selectedValue, 
                         enabledReasoning: isAlwaysUseReasoning ? true : false 
                    } : item)),
               }));
               if (typeof clearUnsavedChangesFlag === 'function') {
                    clearUnsavedChangesFlag();
               }
          },
          [clearUnsavedChangesFlag, modelOptions, setState]
     );

     const handleReasoningChange = useCallback(
          (index, enabledReasoning) => {
               setState((prev) => ({
                    ...prev,
                    modelSelections: prev.modelSelections.map((item, i) => (i === index ? { ...item, enabledReasoning } : item)),
               }));
               if (typeof clearUnsavedChangesFlag === 'function') {
                    clearUnsavedChangesFlag();
               }
          },
          [clearUnsavedChangesFlag, setState]
     );

     const handleAddModel = useCallback(() => {
          setState((prev) => ({
               ...prev,
               modelSelections: [...prev.modelSelections, { id: '', exampleOutput: '', isLoading: false, enabledReasoning: false }],
          }));
          if (typeof clearUnsavedChangesFlag === 'function') {
               clearUnsavedChangesFlag(); // Clear flag only on FULL success
          }
     }, [clearUnsavedChangesFlag, setState]);

     const handleRemoveModel = useCallback((index) => {
          setState((prev) => ({
               ...prev,
               modelSelections: prev.modelSelections.filter((_, i) => i !== index),
          }));
          if (typeof clearUnsavedChangesFlag === 'function') {
               clearUnsavedChangesFlag(); // Clear flag only on FULL success
          }
     }, [clearUnsavedChangesFlag, setState]);

     const handleRunAnalysis = useCallback(
          async (values, index) => {
               try {

                    const { accountId, id: templateId } = stepsData;
                    const modelSelection = state.modelSelections[index];
                    const modelId = modelSelection?.id;
                    const enabledReasoning = modelSelection?.enabledReasoning || false;

                    if (!modelId) {
                         toast.error('Please select a model first');
                         return;
                    }

                    if (usingReportFile && files.filter(Boolean).length === 0) {
                         toast.error('Please upload at least one file');
                         return;
                    }

                     setState((prev) => ({
                         ...prev,
                         modelSelections: prev.modelSelections.map((item, i) => (i === index ? { ...item, isLoading: true } : item)),
                    }));

                    const formData = new FormData();

                    const skipBilling = window.location.search.includes('run-preview-no-cost=1');

                    const formFields = {
                         accountId,
                         templateId,
                         llmModelId: modelId,
                         triggerType: TRIGGER_TYPE.MANUAL_PREVIEW,
                         usingReportFile,
                         reportConfigs: JSON.stringify(
                              reportConfigs.map((item) => ({
                                   id: item.id,
                                   startDate: item.startDate,
                                   endDate: item.endDate,
                              }))
                         ),
                         userId,
                         guidanceOverride: btoa((encodeURIComponent(values.guidanceOverride))),
                         mainInstructions: btoa((encodeURIComponent(values.mainInstructions))),
                         exampleOutputs: btoa((encodeURIComponent(values.exampleOutputs))),
                         enabledReasoning,
                         skipBilling,
                    };

                    // Add basic fields
                    Object.entries(formFields).forEach(([key, value]) => {
                         formData.append(key, value);
                    });

                    // Add files, filtering out nulls
                    files.filter(Boolean).forEach((file) => formData.append('files', file));

                    const response = await callTokenApi(API_AI_INSIGHTS_RUN_ANALYSIS, 'POST', formData);

                    if (response?.data?.code === 200) {
                         setState((prev) => ({
                              ...prev,
                              modelSelections: prev.modelSelections.map((item, i) =>
                                   i === index ? { 
                                        ...item, 
                                        jobId: response.data.jobId, 
                                        exampleOutput: response.data.exampleOutput, 
                                        disabled: true,
                                   } : item
                              ),
                         }));
                         if (typeof setIsReFreshLogs === 'function') setIsReFreshLogs(true);
                    } else {
                         toast.error('Analysis failed. Please try again.');
                    }
               } catch (error) {
                    console.error('Analysis error:', error);
                    toast.error('Failed to run analysis. Please try again.');
                    setState((prev) => ({
                         ...prev,
                         modelSelections: prev.modelSelections.map((item, i) =>
                              i === index ? { ...item, exampleOutput: '', isLoading: false } : item
                         ),
                    }));
               }
               finally {
                    if (typeof clearUnsavedChangesFlag === 'function') {
                         clearUnsavedChangesFlag(); // Clear flag only on FULL success
                    }
               }
          },
          [stepsData, files, state.modelSelections, usingReportFile, reportConfigs, userId, clearUnsavedChangesFlag, setState, setIsReFreshLogs]
     );

     const onSubmit = useCallback(
          async (values) => {
               if (!values.llmModelId) {
                    toast.error('Model is required.');
                    return;
               }

               if (usingReportFile && files.filter(Boolean).length === 0) {
                    toast.error('Please upload at least one file');
                    return;
               }

               setState((prev) => ({ ...prev, isSubmitting: true }));
               try {
                    const currentStep = 5;

                    const markdown = generatedInsightMarkdown(values);

                    const data = {
                         guidanceOverride: values?.guidanceOverride || '',
                         mainInstructions: values?.mainInstructions || '',
                         exampleOutputs: values?.exampleOutputs || '',
                         llmModelId: values?.llmModelId || '',
                         enabledReasoning: values?.enabledReasoning || false,
                         prompt: markdown || '',
                    };

                    const response = await callTokenApi(API_AI_INSIGHTS_TEMPLATES_BY_ID.replace(':id', stepsData.id), 'PUT', {
                         ...data,
                         lastEditStep: stepsData.lastEditStep && stepsData.lastEditStep > currentStep ? stepsData.lastEditStep : currentStep,
                    });

                    if (response?.data?.code === 200) {
                         setActiveStep(6);
                         setStepsData({
                              ...stepsData,
                              ...data,
                              lastEditStep: response?.data?.data?.lastEditStep,
                         });
                         toast.success('Template updated successfully.');
                    } else {
                         toast.error('Failed to update the template. Please try again.');
                    }
               } catch (error) {
                    console.error('Update error:', error);
                    toast.error('Something went wrong while updating. Please try again later.');
               } finally {
                    setState((prev) => ({ ...prev, isSubmitting: false }));

                    if (typeof clearUnsavedChangesFlag === 'function') {
                         clearUnsavedChangesFlag(); // Clear flag only on FULL success
                    }
               }
          },
          [setActiveStep, setStepsData, stepsData, clearUnsavedChangesFlag, usingReportFile, files, setState]
     );

     useEffect(() => {
          const handler = async (data) => {
               
               if (data?.userId !== userId) return;
              
               // Prevent duplicate job handling
               if (!data?.jobId || !state.modelSelections.some((item) => item.jobId === data.jobId)) return;

               try {
                    if (data?.jobId) {
                         const response = await callTokenApi(API_AI_INSIGHTS_JOB_BY_ID.replace(':id', data.jobId), 'GET');
                         if (response?.data?.code === 200) {
                              const { generatedInsightMarkdown, executedPrompt, chargedCredits, inputTokens, outputTokens } = response.data.data;
                              setState((prev) => ({
                                   ...prev,
                                   modelSelections: prev.modelSelections.map((item) =>
                                        item.id === data.llmModelId && item.jobId === data.jobId
                                             ? { ...item, exampleOutput: generatedInsightMarkdown, isLoading: false, executedPrompt: executedPrompt, chargedCredits: chargedCredits, inputTokens: inputTokens, outputTokens: outputTokens }
                                             : item
                                   ),
                              }));
                         } else {
                              toast.error('Failed to load insight details');
                         }
                    }
               } catch (error) {
                    toast.error('An error occurred while loading insight details');
               } finally {
                    const { errorApi } = data;
                    if (errorApi) {
                         toast.error(errorApi);
                    }
                    setState((prev) => ({
                         ...prev,
                         modelSelections: prev.modelSelections.map((item) => (item.id === data.llmModelId ? { ...item, isLoading: false } : item)),
                    }));
               }
          };

          subscribe('preview-ai-example-output', handler);
          return () => unsubscribe('preview-ai-example-output', handler);
     }, [userId, state.modelSelections, setState, setIsReFreshLogs]);

     // Function to check if we can still add more models
     const canAddMoreModels = () => {
          const countOptionsSupportReasoning = modelOptions.filter((modelOption) => modelOption.supportReasoning && !LLM_ALWAYS_USE_REASONING.includes(modelOption.name)).length;
          if (state.modelSelections.length < countOptionsSupportReasoning + modelOptions.length) return true;
          return false
     };

     return (
          <StepCardHeader
               title={`Model Selection & Preview`}
               step={5}
               activeStep={activeStep}
               lastEditStep={stepsData.lastEditStep}
               setActiveStep={() => setActiveStep(5)}
          >
               <p>Preview your entire set of instructions and select up to 3 models to compare the outputs.</p>
               <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
                    {({ values, errors, handleSubmit, dirty, handleChange, setFieldValue }) => {
                         return (
                              <>
                                   <FormikStateObserver dirty={dirty} onDirtyChange={handleSetUnsavedChanges} />

                                   <CForm onSubmit={handleSubmit} noValidate>
                                        <div className="accordion">
                                             <Accordion
                                                  accordionType={ACCORDION_TYPES.INSTRUCTION}
                                                  title="Instructions"
                                                  description="View and edit the entire set of instructions for your analysis."
                                                  accordionShow={state.accordion}
                                                  setAccordionShow={(accordion) => setState((prev) => ({ ...prev, accordion }))}
                                             >
                                                  <InstructionsPreview
                                                       values={values}
                                                       errors={errors}
                                                       handleChange={handleChange}
                                                       setFieldValue={setFieldValue}
                                                       stepsData={stepsData}
                                                  />
                                             </Accordion>
                                             <Accordion
                                                  accordionType={ACCORDION_TYPES.DATA}
                                                  title="Data"
                                                  description="Modify the date range for your sample data, strictly for preview purposes."
                                                  accordionShow={state.accordion}
                                                  setAccordionShow={(accordion) => setState((prev) => ({ ...prev, accordion }))}
                                             >
                                                  <DataPreview
                                                       values={values}
                                                       errors={errors}
                                                       handleChange={handleChange}
                                                       files={files}
                                                       setFiles={setFiles}
                                                       usingReportFile={usingReportFile}
                                                       setUsingReportFile={setUsingReportFile}
                                                       reportConfigs={reportConfigs}
                                                       setReportConfigs={setReportConfigs}
                                                  />
                                             </Accordion>
                                        </div>
                                        <p className="mt-3">
                                             <strong>Select Models for Preview</strong>
                                             <div className="tooltip-wrapper ml-2 d-inline-block">
                                                  <ClickInfoIconTooltip>
                                                       <p>Add and run multiple models to compare their preview results. These selections are for preview purposes only</p>
                                                  </ClickInfoIconTooltip>
                                             </div>
                                        </p>
                                        {state.modelSelections.map((item, index) => {
                                             const { id, exampleOutput, isLoading, executedPrompt, chargedCredits, inputTokens, outputTokens, enabledReasoning } = item;
                                             
                                             const modelSelectionOptions = modelOptions.filter((modelOption) => {
                                                  // Check if this model is not selected yet
                                                  const isNotSelected = !state.modelSelections.some(
                                                       (selection) => selection.id === modelOption.value
                                                  );
                                                  
                                                  const isNonGrok = !LLM_ALWAYS_USE_REASONING.includes(modelOption.name);
                                                  const supportsReasoning = modelOption.supportReasoning && isNonGrok;
                                                  const selectionCount = state.modelSelections.filter(
                                                       (selection) => selection.id === modelOption.value && isNonGrok
                                                  ).length;
                                                  
                                                  // Either show unselected models or reasoning-capable models with fewer than 2 selections
                                                  return isNotSelected || (supportsReasoning && selectionCount < 2);
                                             });

                                             const hasChooseModelSupportReasoning = state.modelSelections.some((stateItem) => {
                                                  return modelOptions.some((modelOption) => {
                                                       return stateItem.id === modelOption.value && modelOption.supportReasoning
                                                  })
                                             });

                                             return (
                                                  <div className="mt-4" key={index}>
                                                       <CRow className="align-items-center mb-2 position-relative">
                                                            <CCol xs="12" md={hasChooseModelSupportReasoning ? '6' : '5'}>
                                                                 <ModelSelect
                                                                      id={`model-${index}`}
                                                                      value={id}
                                                                      options={modelSelectionOptions}
                                                                      modelOptions={modelOptions}
                                                                      onChange={(value) => handleModelChange(index, value)}
                                                                      disabled={item.disabled}
                                                                      enabledReasoning={enabledReasoning}
                                                                      onReasoningChange={(checked) => handleReasoningChange(index, checked)}
                                                                      idPrefix={`preview-${index}-`}
                                                                 />
                                                            </CCol>
                                                            <CCol xs="12" md={hasChooseModelSupportReasoning ? '6' : '7'}>
                                                                 <CButton
                                                                      className="d-inline deleteicon px-0 py-0"
                                                                      onClick={() => handleRemoveModel(index)}
                                                                      disabled={isLoading}
                                                                 >
                                                                      <CIcon icon="iconDeleteField" className="icon-delete" />
                                                                 </CButton>
                                                                 {id && (
                                                                      <CButton
                                                                           className="text-capitalize ml-3"
                                                                           color="link"
                                                                           type="button"
                                                                           onClick={() => handleRunAnalysis(values, index)}
                                                                           disabled={isLoading || exampleOutput || isLoadingAvailableReports}
                                                                      >
                                                                           {isLoading ? 'Running...' : 'Run Analysis'}
                                                                      </CButton>
                                                                 )}

                                                                 {exampleOutput && (
                                                                      <CButton
                                                                           className="text-capitalize ml-3"
                                                                           color="link"
                                                                           type="button"
                                                                           onClick={() => setState((prev) => ({
                                                                                ...prev,
                                                                                modelSelections: prev.modelSelections.map((item, i) =>
                                                                                     i === index ? { 
                                                                                          ...item, 
                                                                                          exampleOutput: '',
                                                                                     } : item
                                                                                ),
                                                                           }))}
                                                                      >
                                                                           Clear the Preview
                                                                      </CButton>
                                                                 )}
                                                            </CCol>
                                                            {isLoading && (
                                                                 <div className="ai-insights-preview-loading">
                                                                      <CenterSpinner />
                                                                 </div>
                                                            )}
                                                       </CRow>

                                                       <ModelOutput
                                                            jobId={item.jobId}
                                                            output={exampleOutput}
                                                            index={index}
                                                            isLoading={isLoading}
                                                            executedPrompt={executedPrompt}
                                                            chargedCredits={chargedCredits}
                                                            inputTokens={inputTokens}
                                                            outputTokens={outputTokens}
                                                       />
                                                  </div>
                                             );
                                        })}
                                        {canAddMoreModels() ? (
                                             <CButton type="button" className="add-icon d-flex align-items-center mb-3" onClick={handleAddModel}>
                                                  <CIcon icon="iconAddField" className="icon-add" />
                                                  <span className="pl-2 f-13">Add Model</span>
                                             </CButton>
                                        ) : (
                                             <div className="p-2"></div>
                                        )}
                                        <hr className="my-4" />
                                        <p className="my-3">
                                             <strong>Select a final model to use with this AI Insights Template.</strong>
                                        </p>
                                        <CRow className="align-items-center mb-4">
                                             <CCol xs="12" md="6">
                                                  <ModelSelect
                                                       id="llmModelId"
                                                       value={values.llmModelId}
                                                       options={modelOptions}
                                                       modelOptions={modelOptions}
                                                       onChange={(value) => {
                                                            const selectedModel = modelOptions.find(opt => opt.value === value);
                                                            const isAlwaysUseReasoning = LLM_ALWAYS_USE_REASONING.includes(selectedModel?.value);
                                                            setFieldValue('llmModelId', value);
                                                            if (isAlwaysUseReasoning) {
                                                                 setFieldValue('enabledReasoning', true);
                                                            } else {
                                                                 setFieldValue('enabledReasoning', false);
                                                            }
                                                       }}
                                                       enabledReasoning={values.enabledReasoning}
                                                       onReasoningChange={(checked) => setFieldValue('enabledReasoning', checked)}
                                                       idPrefix="final-"
                                                  />
                                             </CCol>
                                        </CRow>
                                        <CRow className="align-items-center mb-2">
                                             <CCol>
                                                  <CButton type="submit" className="px-4" color="primary" disabled={state.isSubmitting || (usingReportFile && files.filter(Boolean).length === 0)}>
                                                       <Waiting isLoading={state.isSubmitting}>Save & Next</Waiting>
                                                  </CButton>
                                             </CCol>
                                        </CRow>
                                   </CForm>
                              </>
                         );
                    }}
               </Formik>
          </StepCardHeader>
     );
};

export default React.memo(ModelSelectionPreview);
